import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef
} from "@angular/core";
import {
  NzTableFilterFn,
  NzTableQueryParams,
  NzTableSortFn
} from 'ng-zorro-antd/table';
import { NzResizeEvent } from 'ng-zorro-antd/resizable';
import { Observable } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NzModalService } from "ng-zorro-antd/modal";
import { TableExportToExcelComponent } from "@shared/components/tables/table-export-to-excel.component";
import { mapToObject } from "@shared/utils/object-mapper.util";

export enum FilterType {
  DROPDOWN = 'dropdown',
  TEXT = 'text',
  DATE = 'date',
  DATE_RANGE = 'dateRange'
}

export interface TableColumn {
  title: string;
  key: string;
  align?: 'left' | 'center' | 'right';
  comp?:  'button' | 'text' | 'tags' | 'icon' | 'avatar' | 'cellTemplate';
  render?: (row: any) => any;
  value?: string;
  visible?: boolean;
  alwaysVisible?: boolean;
  sortable?: boolean;
  sortFn?: NzTableSortFn<any> | boolean;
  sortOrder?: 'ASC' | 'DESC' | null;
  filterEnabled?: boolean;
  filterType?: FilterType;
  filterOptions?: { text: any; value: any; selected: any; disabled?: boolean}[]; // Dropdown seçenekleri için
  filterValue?: any; // Seçilen/girilen filtre değeri
  filterFn?: NzTableFilterFn<any> | boolean;
  resizable?: boolean;
  expand?: boolean;
  width?: string | number;
  fixed?:  'left' | 'right';
  columnClass?: string;
  buttons?:any[]
}

export interface FilterEvent {
  key: string;
  value: any;
  filterType?: FilterType;
}

interface TableState {
  columnStates: {
    key: string;
    visible: boolean;
    width: string | number;
  }[];
}

export interface TableQueryParams {
  pageIndex?: number;
  pageSize?: number;
  sort?: string[];
  filter?: { [key: string]: string[] | null };
}

@Component({
  selector: 'app-tables',
  templateUrl: './tables.component.html',
  styleUrls: ['./tables.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TablesComponent implements OnInit {

  constructor(
    private sanitizer: DomSanitizer,
    private modalService: NzModalService,
    private cdr: ChangeDetectorRef

  ) {
    this.internalTableId = this.generateUUID();
  }
  @Input() queryParams: any;
  @Input() columns: TableColumn[] = [];
  // @Input() loading = false;
  @Input() pageSize = 10;
  @Input() selectedDate: any;
  @Input() tableStateId = '';
  @Input() topBarPosition = 'right';
  @Input() nzQueryParams: TableQueryParams = {};
  @Input() width: any
  @Input() height: any = "240px";
  @Input() showExportButton? = false;
  @Input() showClearFiltersButton? = false;
  @Input() remoteLoadFn?: (params: NzTableQueryParams) => Observable<any>;
  @Input() frontPagination = false;
  @Input() dropdownMenuTemplate?: TemplateRef<any>; // Dropdown menu template'i için yeni Input
  @Input() columnDropdownTemplates?: { [key: string]: TemplateRef<any> };
  @Input() createExportUrl: string;
  @Input() downloadExportUrl: string;
  @Input() expandRowTemplate?: TemplateRef<any>;
  @Input() customTabBar?: TemplateRef<any>;
  @Input() cellTemplate?: TemplateRef<any>;
  @Input() defaultTableFilter?: any;
  @Input() panel = false;
  @Input() panelTitle? = "";
  @Input() tableLayout?:'auto'|'fixed'='fixed'
  @Output() filterChange = new EventEmitter<FilterEvent>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() showSizeChanger = true;



  loading=false;
  total = 0;
  data: any[] = [];
  allChecked = true;
  isResizing = false;

  exportButtonLoadingTeamStatistic = false;
  searchValue = '';
  searchInputState = false;
  currentParamsString = ''
  visibleColumns: TableColumn[] = [];
  currentPage = 1;
  FilterType = FilterType; // template'de kullanabilmek için

  barStyle = {
    float: this.topBarPosition
  };

  private readonly STORAGE_KEY = 'table_state_';
  private internalTableId: string;
  protected readonly scroll = scroll;
  private currentFilterKey = '';

  // Tüm aktif filtreleri tutacak map
  private activeFilters: Map<string, any> = new Map();
  private activeSort: Map<string, any> = new Map();


  currentColumn: TableColumn | null = null;
  selectedDropdownValues: Set<any> = new Set();
  customFilterVisible: { [key: string]: boolean } = {};

  protected readonly parseInt = parseInt;
  protected readonly parseFloat = parseFloat;

  protected readonly Array = Array;

  ngOnInit(): void {
    const effectiveTableId = this.tableStateId || this.internalTableId;
    const savedState = this.loadState(effectiveTableId);

    if (this.defaultTableFilter && typeof this.defaultTableFilter === 'object') {
      Object.keys(this.defaultTableFilter).forEach((key: string) => {
        this.activeFilters.set(key, this.defaultTableFilter[key]);
      });
    }

    if (savedState) {
      this.applyState(savedState);
    } else {
      this.initializeColumns();
    }
  }

  updateTable() {
    this.cdr.detectChanges();
  }

  // Aşağıdaki generateUUID teorik olarak gerekli bir method değil. Sadece parent componentten bize tableStateId gelmezse patlamasın diye unique id generate etmeye yarıyor.
  // Generate edilen unique id constructor içerisinde internalTableId'ye veriliyor. internalTableId onInit içinde

  private generateUUID(): string {
    // tslint:disable-next-line:only-arrow-functions
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.floor(Math.random() * 16);
      const v = c === 'x' ? r : (r % 4) + 8;
      return v.toString(16);
    });
  }

  private initializeColumns(): void {
    this.visibleColumns = this.columns.map(col => ({
      ...col,
      visible: col.visible !== undefined ? col.visible : true,
      filterType: this.determineFilterType(col),
      align: col.align || 'left',
    }));

    const visibleColLength = this.visibleColumns.filter(col => col.visible).length;
    this.allChecked = visibleColLength === this.visibleColumns.length;
  }

  downloadExportData() {
    const selectedFilters = mapToObject(this.activeFilters.entries());
    this.modalService.create({
      nzMaskClosable: false,
      nzContent: TableExportToExcelComponent,
      nzWidth: '950px',
      nzFooter: null,
      nzComponentParams: {
        title: 'Team Statistic Report Export',
        queryParams: { ...this.queryParams, selectedFilters },
        createExportUrl: this.createExportUrl,
        downloadExportUrl: this.downloadExportUrl
        // Yukarıdan gönderilen örneğin widgetEnum değilde widgetEnums gönderilebilir böyle olursa patlar. patlamaması için bütün parametreleri aldık, sana bir parametre set edilmişse onları kullan dedik
      }
    });
  }

  // generic table'a custom css yeteneği kazandırmış olduk.
  getColumnsClass(col: any){
    const classes: string[] = [col.columnClass];
    if(!this.isResizing){
      classes.push('col-resizing');
    }
    return classes.join(' ') // burası classları string yapıyor ve yanyana yapıyor
  }

  private determineFilterType(column: TableColumn): FilterType | undefined {
    if (!column.filterEnabled) {
      return undefined;
    }

    if (column.filterOptions && column.filterOptions.length > 0) {
      return FilterType.DROPDOWN;
    }

    return column.filterType; // Eğer filterOptions yoksa, verilen filterType'ı kullan
  }



  isExpandRowTemplate(): boolean {
    return !!this.expandRowTemplate; // null veya undefined değilse true döner
  }

  onRowClick(event, row,index){
    this.rowClick.emit( {  event, row,index} )
  }

  onFilterChange(event: any) {
    // Dropdown filtreler için
    if (Array.isArray(event)) {
      const column = this.visibleColumns.find(col => col.key === this.currentFilterKey);
      if (event.length > 0) {
        this.activeFilters.set(this.currentFilterKey, event);
      } else {
        this.activeFilters.delete(this.currentFilterKey);
      }
    }

    this.applyAllFilters();
  }

  onChangeSort(event, column: any){
    this.activeSort.set(column.key, event);
  }

  search(): void {
    // Custom filtreler için
    if (this.searchValue) {
      this.activeFilters.set(this.currentFilterKey, [this.searchValue]);
    } else {
      this.activeFilters.delete(this.currentFilterKey);
    }
    this.customFilterVisible[this.currentFilterKey] = false;
    this.applyAllFilters();
  }

  reset(): void {
    this.searchValue = '';
    this.activeFilters.delete(this.currentFilterKey);
    this.customFilterVisible[this.currentFilterKey] = false;
    this.applyAllFilters();
  }

  private applyAllFilters(): void {
    // Tüm aktif filtreleri birleştir
    const filters = Array.from(this.activeFilters.entries()).map(([key, value]) => ({
      key,
      value
    }));

    const sort = Array.from(this.activeSort.entries()).map(([key, value]) => ({
      key,
      value
    }));

    this.onQueryParamsChange({
      pageIndex: 1,
      pageSize: this.pageSize,
      filter: filters,
      sort
    });
  }

  // Tüm filtreleri temizle
  clearAllFilters(): void {
    this.activeSort.clear();
    this.activeFilters.clear();
    this.searchValue = '';
    this.applyAllFilters();

    this.columns.forEach(col => {
      if (col.filterOptions) {
        col.filterOptions.forEach((option: any) => option.selected = false);
        col.sortFn = false;
      }
    });

    this.selectedDropdownValues.clear();
  }

  onFilterDropdownOpen(visible: boolean, col: TableColumn): void {
    this.currentColumn = col;
    this.currentFilterKey = col.key;

    // Dropdown için mevcut seçili değerleri ayarla
    if (col.filterType === FilterType.DROPDOWN) {
      const currentFilter = this.activeFilters.get(col.key);
      this.selectedDropdownValues = new Set(currentFilter || []);

      // filterOptions'ları güncelle
      col.filterOptions?.forEach((option:any) => {
        option.selected = this.selectedDropdownValues.has(option.value);
      });
    } else {
      this.searchValue = this.currentFilterKey !== col.key ? '' : this.searchValue;
    }
    this.customFilterVisible[col.key] = true;
  }

  onDropdownFilterChange(option: any): void {
    if (!this.currentColumn || !this.currentColumn.filterOptions) {
      return;
    }

    if (option.selected) {
      this.selectedDropdownValues.add(option.value);
    } else {
      this.selectedDropdownValues.delete(option.value);
    }

    // NOT: Organization -> Team Activities Report için isFilterOpen parametresine bağlı olarak kontrol gerçekleştirildi.

    const filterOptions = this.currentColumn.filterOptions;
    const isFirstOpenOption = filterOptions.find(o => o.value === 'isFirstOpen');

    // Tüm seçilenleri kontrol et (isFirstOpen dahil)
    const selectedCount = filterOptions.filter(o => o.selected).length;
    const hasOtherSelections = filterOptions.some(o => o.value !== 'isFirstOpen' && o.selected);

    if (isFirstOpenOption) {
      if (selectedCount === 0) {
        isFirstOpenOption.disabled = false;
      } else if (hasOtherSelections) {
        isFirstOpenOption.disabled = true;
        isFirstOpenOption.selected = false;
        this.selectedDropdownValues.delete('isFirstOpen');
      } else {
        isFirstOpenOption.disabled = false;
      }
    }
  }

  resetDropdownFilter(): void {
    this.selectedDropdownValues.clear();

    if (this.currentColumn?.filterOptions) {
      this.currentColumn.filterOptions.forEach((option: any) => {
        option.selected = false;

        // isFirstOpen varsa, tekrar enable yap
        if (option.value === 'isFirstOpen') {
          option.disabled = false;
        }
      });
    }

    this.activeFilters.delete(this.currentFilterKey);
    this.customFilterVisible[this.currentFilterKey] = false;
    this.applyAllFilters();
  }

  applyDropdownFilter(): void {
    const values = Array.from(this.selectedDropdownValues);
    if (values.length > 0) {
      this.activeFilters.set(this.currentFilterKey, values);
    } else {
      this.activeFilters.delete(this.currentFilterKey);
    }
    this.customFilterVisible[this.currentFilterKey] = false;
    this.applyAllFilters();
  }

  canHideColumn(column: TableColumn): boolean {
    if(column.alwaysVisible){
      return false;
    }
    return !column.visible || this.visibleColumns.filter(col => col.visible).length > 1;
  }


  updateAllChecked(): void {
    this.visibleColumns = this.visibleColumns.map((column: TableColumn) => {
      column.visible = this.allChecked;
      return column;
    })
    this.visibleColumns[0].visible = true
    const visibleColLength = this.visibleColumns.filter(col => col.visible).length;
    this.allChecked = visibleColLength === this.visibleColumns.length;
  }

  onResizeStart(): void {
    this.isResizing = true;
  }


  onResize({ width }: NzResizeEvent, column: TableColumn): void {
    if (width) {
      column.width = `${width}px`;
      this.saveState();
    }

    this.isResizing = false;
  }

  toggleColumnVisibility(column: TableColumn): void {
    this.saveState();
    const visibleColLength = this.visibleColumns.filter(col => col.visible).length;
    this.allChecked = visibleColLength === this.visibleColumns.length;
  }

  private loadState(tableId: string): TableState | null {
    try {
      const savedState = localStorage.getItem(this.STORAGE_KEY + tableId);
      return savedState ? JSON.parse(savedState) : null;
    } catch (error) {
      return null;
    }
  }

  private saveState(): void {
    const effectiveTableId = this.tableStateId || this.internalTableId;
    try {
      const state: TableState = {
        columnStates: this.visibleColumns.map(col => ({
          key: col.key,
          visible: col.visible ?? true,
          width: col.width ?? 'auto'
        }))
      };
      localStorage.setItem(
        this.STORAGE_KEY + effectiveTableId,
        JSON.stringify(state)
      );
    } catch (error) {
      console.error('Error saving table state:', error);
    }
  }

  private applyState(state: TableState): void {
    this.visibleColumns = this.columns.map(col => {
      const savedState = state.columnStates.find(s => s.key === col.key);
      return {
        ...col,
        visible: savedState?.visible ?? col.visible ?? true,
        width: savedState?.width ?? col.width,
        filterType: this.determineFilterType(col),
        filterOptions: col.filterOptions,
      };
    });

    const visibleColLength = this.visibleColumns.filter(col => col.visible).length;
    this.allChecked = visibleColLength === this.visibleColumns.length;
  }

  loadData(params: any) {
    const QueryParams = { ...this.nzQueryParams, ...params ,selectedFilters: params.filter, ...this.queryParams};
    this.loading = true;
    // data hemen gelince degisiklik fark edilemiyor o yuzde loading icin timeout eklendi
    setTimeout(() => {
      this.remoteLoadFn(QueryParams).subscribe((response:any) => {
        /**
         * { "data:"[...], "pagination": { "total": 100, "size": 10, "page": 1 }}
         */
        this.data = response.data;
        this.total = response?.pagination?.total;
        this.pageSize = response?.pagination?.size;
        this.loading = false;
        this.updateTable()
      });
    }, 300);
  }

  getParamFilters() : any {
    let myFilter = {};
    Array.from(this.activeFilters.entries()).map(([key, value]) => ({
      [key]: value,
    })).forEach(item => {
      myFilter = {...myFilter, ...item};
    })
    return this.activeFilters.size > 0 ? myFilter : null;
  }

  onQueryParamsChange(params: any): void {
    // bu params nztable'ın bize verdiği değerler

    const _params = { ...params };
    // this.activeFilters payloadda gönderdiğimiz selectedFilters'a denk geliyor.
    _params.filter = this.getParamFilters()

    // filtrelerde number için filtre yapılabiliyor mu araştır

    const allColumnsSortFixed = { ..._params, sort: params?.sort?.filter(item => item.value != null) };

    if (this.currentParamsString === JSON.stringify(allColumnsSortFixed)) {
      return;
    }
    this.currentParamsString = JSON.stringify(allColumnsSortFixed);
    this.loadData(allColumnsSortFixed);
  }

  // HTML içeriğini güvenli hale getiren yardımcı metod
  getSafeHtml(column: any,row:any): SafeHtml {
    let content = '';
    try {
      content = column.render(row)
      if (typeof content === 'string' && (content.includes('<') || content.includes('>'))) {
        return this.sanitizer.bypassSecurityTrustHtml(content);
      }
    }catch (e) {
      console.error(e);
    }
    return content;
  }

  isFilterActive(column: any): boolean {
    return column.filterOptions?.some((option: any) => option.selected);
  }

  isCustomFilterActive(column: any): boolean {
    const activeValue = this.activeFilters.get(column.key);
    return Array.isArray(activeValue) && activeValue.length > 0 && activeValue[0].trim() !== '';
  }

  isDropdownFilterActive(): boolean {
    //
    setTimeout(() => {
      this.searchInputState = this.searchValue === '';
    }, 0);
    return this.currentColumn?.filterOptions?.some(option => option.selected) || false;
  }


  isVisible(visible: boolean | ((param:any) => boolean),params?:any): boolean {
    if (typeof visible === 'function') {
      return visible(params);
    }
    return visible;
  }

}
